// #7: tamaño de letra / interfaz configurable (por dispositivo). Aplica un `zoom` al documento, que
// escala TODA la app (incluido el chat flotante). Persistido en localStorage.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  B-868 · QUE EL TAMAÑO SE COMPORTE COMO EL ZOOM DEL NAVEGADOR, NO COMO UNA LUPA        (25-sep-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «desde el perfil del usuario actualmente hay una opción para hacer mas grande las letras/iconos/etc o mas
//  pequeñas, pero no es mas que un zoom, y lo que se necesita es que sea adaptativo de verdad toda la app».
//
//  LO QUE PASABA, MEDIDO en Chromium y en WebKit (el motor del iPhone) con `zoom: 1.25` en <html> y una ventana de 1280:
//   · la ventana seguía midiendo 1280 y los cortes de móvil/tablet (useIsMobile, @media) no saltaban: se pintaba el
//     escritorio entero en un sitio que, a ese tamaño, solo tiene 1024 de ancho útil → se salía por la derecha;
//   · `100vw` pintaba 1600 y `100vh` 1000: lo que medía «toda la pantalla» se salía un 25% por la derecha y por abajo;
//   · lo que se mide de la pantalla (ratón, getBoundingClientRect, innerWidth) llega en px DE PANTALLA, y lo que se
//     escribe en un estilo (left, top) se pinta MULTIPLICADO por el zoom: los menús flotantes caían desplazados.
//  El zoom del navegador (Ctrl +) no tiene ninguno de los tres problemas porque ENCOGE la ventana en px de CSS. Esto
//  hace lo mismo desde dentro:
//   1. ANCHO EFECTIVO = ventana / zoom. Es el que miran useIsMobile, los cortes de las hojas de estilo (como clases en
//      <html>, ver CORTES) y cualquier «¿cabe?» de la app.
//   2. `--z` en <html>: las medidas de pantalla completa se escriben `calc(100vh / var(--z, 1))` (medido: da 800 y 1280
//      con zoom 1.25 en los dos motores; las unidades de contenedor NO valen, en WebKit fallan).
//   3. aPxDeEstilo(): lo medido en pantalla se divide por el zoom antes de escribirlo en left/top/width/height.
//  Cambiar el tamaño en el perfil avisa con el evento EVENTO_ZOOM: lo que depende del ancho se recalcula sin recargar.
//  Lo mide backend/routes/tamano-adaptativo.test.js.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { marca } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const KEY = 'app_zoom'

// [valor, etiqueta]. '1' = normal (sin zoom).
export const ZOOMS = [
  ['0.9', marca('utils.uiScale.pequena', 'Pequeña')],
  ['1', marca('utils.uiScale.normal', 'Normal')],
  ['1.1', marca('utils.uiScale.grande', 'Grande')],
  ['1.25', marca('utils.uiScale.muy_grande', 'Muy grande')],
]

export const EVENTO_ZOOM = 'biango:zoom'

export function getZoom() {
  try { return localStorage.getItem(KEY) || '1' } catch { return '1' }
}

// El zoom que tiene puesto AHORA la página (1 si no hay, o si no se puede leer).
export function zoomUI(doc) {
  try {
    const d = doc || (typeof document !== 'undefined' ? document : null)
    const z = parseFloat((d && d.documentElement && d.documentElement.style.zoom) || '1')
    return z > 0 && Number.isFinite(z) ? z : 1
  } catch (_) { return 1 }
}

// El ancho y el alto útiles, en px de CSS: la ventana dividida por el zoom (lo que vería el zoom del navegador).
export function anchoEfectivo(win) {
  const w = win || (typeof window !== 'undefined' ? window : null)
  if (!w) return 1280
  return (Number(w.innerWidth) || 0) / zoomUI(w.document)
}
export function altoEfectivo(win) {
  const w = win || (typeof window !== 'undefined' ? window : null)
  if (!w) return 800
  return (Number(w.innerHeight) || 0) / zoomUI(w.document)
}

// De px de pantalla (ratón, getBoundingClientRect, innerWidth) a px de estilo (lo que se escribe en left/top/width).
export function aPxDeEstilo(px, doc) {
  const n = Number(px)
  return Number.isFinite(n) ? n / zoomUI(doc) : px
}

// Los cortes que usan las hojas de estilo (App.css, index.css, Layout, Login), como clases en <html> calculadas con el
// ANCHO EFECTIVO. Van por parejas complementarias (a-max-768 / a-min-769): con zoom el ancho sale con decimales y dos
// comparaciones sueltas (<= 768 y >= 769) dejarían un hueco en 768,8 donde no valdría ninguna de las dos.
export const CORTES = {
  'a-max-768': (w) => w <= 768,
  'a-min-769': (w) => w > 768,
  'a-max-899': (w) => w < 900,
  'a-min-900': (w) => w >= 900,
  'a-max-900': (w) => w <= 900,
  'a-max-1024': (w) => w <= 1024,
}
export function ponerCortes(doc, win) {
  try {
    const d = doc || document
    const ancho = anchoEfectivo(win || (d && d.defaultView) || window)
    for (const [clase, cumple] of Object.entries(CORTES)) d.documentElement.classList.toggle(clase, cumple(ancho))
  } catch (_) {}
}

export function aplicarZoom(z) {
  try {
    const h = document.documentElement
    const valor = (z && z !== '1') ? z : ''
    h.style.zoom = valor
    h.style.setProperty('--z', valor || '1')
    ponerCortes()
    window.dispatchEvent(new Event(EVENTO_ZOOM))
  } catch (_) {}
}

// Una vez al arrancar: al cambiar el tamaño de la ventana (o girar la tablet), los cortes se recalculan.
let vigilando = false
export function vigilarCortes() {
  if (vigilando) return
  vigilando = true
  try { window.addEventListener('resize', () => ponerCortes()); ponerCortes() } catch (_) {}
}

export function setZoom(z) {
  try { localStorage.setItem(KEY, z) } catch (_) {}
  aplicarZoom(z)
}
